import { apolloClient } from '@/core/apollo/client';
import {
  ClaimInviteDocument,
  ClaimInviteResult,
  CompleteInviteResponseDocument,
  CompleteInviteResponseMutation,
  CreateInviteDocument,
  InviteResponseStatusEnum,
  MyInviteReferralsDocument,
  MyInviteReferralsQuery,
  RecordInviteClickDocument,
} from '@/core/graphql/types';
import {
  INVITE_CLAIMER_RESULT_LS_KEY,
  INVITE_COMPLETE_MAX_RETRIES,
  INVITE_COMPLETE_RETRY_DELAY_MS,
  INVITE_CONTENT_PATH_LS_KEY,
  INVITE_ID_LS_KEY,
  INVITE_REFERRAL_LAST_NOTIFIED_ID_LS_KEY,
  INVITE_WELCOME_SEEN_LS_KEY,
} from '@/modules/invite/constants';
import { resolveContentPathFromUrl } from '@/modules/invite/utils/content-path';
import { i18n } from '@/core/plugins/i18n';
import { showTost } from '@/shared/components/core/TosterCenter/tools';
import { AppScreens } from '@/shared/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { RouteLocationNormalized } from 'vue-router';
import AcceptedInviteTostIcon from '@/modules/invite/components/AcceptedInviteTostIcon.vue';

export type InviteAuthRedirect = false | string | { name: AppScreens };

type InviteReferralItem = MyInviteReferralsQuery['myInviteReferrals'][number];

const parseClaimResultFromStorage = (): ClaimInviteResult | null => {
  const raw = localStorage.getItem(INVITE_CLAIMER_RESULT_LS_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as ClaimInviteResult;
  } catch {
    return null;
  }
};

const sleep = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

const getLastNotifiedReferralId = (): string | null => {
  return localStorage.getItem(INVITE_REFERRAL_LAST_NOTIFIED_ID_LS_KEY);
};

const setLastNotifiedReferralId = (referralId: string) => {
  localStorage.setItem(INVITE_REFERRAL_LAST_NOTIFIED_ID_LS_KEY, referralId);
};

const getNewestReferral = (referrals: InviteReferralItem[]): InviteReferralItem | null => {
  if (referrals.length === 0) {
    return null;
  }

  return referrals.reduce((newest, current) => {
    return new Date(current.createdAt).getTime() >= new Date(newest.createdAt).getTime()
      ? current
      : newest;
  });
};

/** Referrals newer than the stored marker, newest-first. */
const getNewReferralsSinceLastNotified = (
  referrals: InviteReferralItem[],
  lastNotifiedId: string,
): InviteReferralItem[] => {
  const lastNotified = referrals.find((referral) => referral.id === lastNotifiedId);
  if (!lastNotified) {
    return [];
  }

  const lastNotifiedTime = new Date(lastNotified.createdAt).getTime();

  return referrals
    .filter((referral) => {
      if (referral.id === lastNotifiedId) {
        return false;
      }

      return new Date(referral.createdAt).getTime() >= lastNotifiedTime;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

const getReferralFirstNames = (referrals: InviteReferralItem[]): string[] => {
  return referrals
    .map((referral) => referral.acceptedUser?.firstName?.trim() ?? '')
    .filter((name) => name.length > 0);
};

/**
 * Toast copy depends on how many new acceptances there are and whether names are available:
 * with names → one / two / three / many; without names → someone / some_many.
 */
const showInviteAcceptedTost = (newReferrals: InviteReferralItem[]) => {
  const count = newReferrals.length;
  if (count === 0) {
    return;
  }

  const names = getReferralFirstNames(newReferrals);
  const newest = getNewestReferral(newReferrals);
  const t = i18n.global.t;
  const toastId = `invite-accepted-${newest?.id ?? newReferrals[0].id}`;
  const toastTo = {
    name: AppScreens.InviteVillage,
  };

  const showNamed = (title: string, description: string) => {
    showTost({
      id: toastId,
      title,
      description,
      to: toastTo,
      icon: AcceptedInviteTostIcon,
    });
  };

  const showAnonymous = (title: string, description: string) => {
    showTost({
      id: toastId,
      title,
      description,
      to: toastTo,
    });
  };

  if (count === 1 && names.length >= 1) {
    showNamed(
      t('village.notification.invite_accepted.one.title', {
        FirstName: names[0],
      }),
      t('village.notification.invite_accepted.one.subtitle'),
    );

    return;
  }

  if (count === 2 && names.length >= 2) {
    showNamed(
      t('village.notification.invite_accepted.two.title', {
        userOneName: names[0],
        userTwoName: names[1],
      }),
      t('village.notification.invite_accepted.two.subtitle'),
    );

    return;
  }

  if (count === 3 && names.length >= 2) {
    showNamed(
      t('village.notification.invite_accepted.three.title', {
        userOneName: names[0],
        userTwoName: names[1],
      }),
      t('village.notification.invite_accepted.three.subtitle'),
    );

    return;
  }

  if (count >= 4 && names.length >= 1) {
    showNamed(
      t('village.notification.invite_accepted.many.title', {
        name: names[0],
        count: count - 1,
      }),
      t('village.notification.invite_accepted.many.subtitle'),
    );

    return;
  }

  if (count === 1) {
    showAnonymous(
      t('village.notification.invite_accepted.someone.title'),
      t('village.notification.invite_accepted.someone.subtitle'),
    );

    return;
  }

  showAnonymous(
    t('village.notification.invite_accepted.some_many.title', {
      count,
    }),
    t('village.notification.invite_accepted.some_many.subtitle'),
  );
};

/**
 * Shows a toast when newer InviteReferral rows appear than the LS marker.
 * First sync / account switch — only seeds the marker, no toast.
 * Multiple new ones → a single toast whose copy depends on count and available names.
 */
const notifyAboutNewestInviteReferralIfNeeded = (referrals: InviteReferralItem[]) => {
  const newest = getNewestReferral(referrals);
  if (!newest) {
    return;
  }

  const lastNotifiedId = getLastNotifiedReferralId();
  const lastNotifiedStillPresent = lastNotifiedId
    ? referrals.some((referral) => referral.id === lastNotifiedId)
    : false;

  if (!lastNotifiedId || !lastNotifiedStillPresent) {
    setLastNotifiedReferralId(newest.id);

    return;
  }

  const newReferrals = getNewReferralsSinceLastNotified(referrals, lastNotifiedId);
  if (newReferrals.length === 0) {
    return;
  }

  showInviteAcceptedTost(newReferrals);
  setLastNotifiedReferralId(newest.id);
};

export const useInviteStore = defineStore('inviteStore', () => {
  const lastClaimResult = ref<ClaimInviteResult | null>(null);
  const inviteReferrals = ref<MyInviteReferralsQuery['myInviteReferrals']>([]);
  let referralsSynced = false;

  const getStoredInviteId = (): string | null => localStorage.getItem(INVITE_ID_LS_KEY);

  const getStoredContentPath = (): string | null => localStorage.getItem(INVITE_CONTENT_PATH_LS_KEY);

  const isInviteWelcomeSeen = (): boolean => localStorage.getItem(INVITE_WELCOME_SEEN_LS_KEY) === 'true';

  const markInviteWelcomeSeen = () => {
    localStorage.setItem(INVITE_WELCOME_SEEN_LS_KEY, 'true');
  };

  const getClaimResult = (): ClaimInviteResult | null => {
    return lastClaimResult.value ?? parseClaimResultFromStorage();
  };

  const shouldShowWelcome = computed(() => {
    return getClaimResult()?.status === InviteResponseStatusEnum.PENDING && !isInviteWelcomeSeen();
  });

  const resetClaimCache = () => {
    lastClaimResult.value = null;
    localStorage.removeItem(INVITE_CLAIMER_RESULT_LS_KEY);
  };

  const clearInviteStorage = () => {
    localStorage.removeItem(INVITE_ID_LS_KEY);
    localStorage.removeItem(INVITE_CONTENT_PATH_LS_KEY);
    localStorage.removeItem(INVITE_CLAIMER_RESULT_LS_KEY);
    localStorage.removeItem(INVITE_WELCOME_SEEN_LS_KEY);
    lastClaimResult.value = null;
  };

  const saveClaimResult = (result: ClaimInviteResult | null) => {
    lastClaimResult.value = result;

    if (!result?.status) {
      localStorage.removeItem(INVITE_CLAIMER_RESULT_LS_KEY);

      return;
    }

    localStorage.setItem(INVITE_CLAIMER_RESULT_LS_KEY, JSON.stringify(result));

    if (
      result.status === InviteResponseStatusEnum.SUCCESS_NEW_USER ||
      result.status === InviteResponseStatusEnum.IGNORED_EXISTING_USER
    ) {
      localStorage.removeItem(INVITE_ID_LS_KEY);
    }
  };

  const persistInviteParams = (inviteId: string | null, contentPath?: string | null) => {
    if (!inviteId) {
      return;
    }

    const existingInviteId = getStoredInviteId();
    if (existingInviteId && existingInviteId !== inviteId) {
      resetClaimCache();
      localStorage.removeItem(INVITE_WELCOME_SEEN_LS_KEY);
    }

    localStorage.setItem(INVITE_ID_LS_KEY, inviteId);

    if (contentPath) {
      localStorage.setItem(INVITE_CONTENT_PATH_LS_KEY, contentPath);
    }
  };

  const recordInviteClick = async (inviteId: string) => {
    try {
      await apolloClient.mutate({
        mutation: RecordInviteClickDocument,
        variables: {
          inviteId,
        },
      });
    } catch (error) {
      console.error('Failed to record invite click', error);
    }
  };

  const persistInviteFromUrl = (url: string) => {
    if (!url) {
      return;
    }

    try {
      const urlObject = new URL(url, window.location.origin);
      const inviteId = urlObject.searchParams.get('inviteId');
      const contentPath = resolveContentPathFromUrl(urlObject);

      if (inviteId) {
        persistInviteParams(inviteId, contentPath);
        void recordInviteClick(inviteId);
      }
    } catch (error) {
      console.error('Failed to persist invite from URL', url, error);
    }
  };

  const createInvite = async (contentPath?: string | null) => {
    const { data } = await apolloClient.mutate({
      mutation: CreateInviteDocument,
      variables: {
        input: {
          contentPath: contentPath || null,
        },
      },
    });

    return data?.createInvite ?? null;
  };

  const claimStoredInvite = async (): Promise<ClaimInviteResult | null> => {
    const inviteId = getStoredInviteId();
    if (!inviteId) {
      return null;
    }

    const cachedResult = getClaimResult();
    if (cachedResult?.status === InviteResponseStatusEnum.PENDING) {
      return cachedResult;
    }

    try {
      const { data } = await apolloClient.mutate({
        mutation: ClaimInviteDocument,
        variables: {
          input: {
            inviteId,
          },
        },
      });
      const result = data?.claimInvite ?? null;
      saveClaimResult(result);

      if (result?.status === InviteResponseStatusEnum.IGNORED_EXISTING_USER) {
        clearInviteStorage();
        if (result.contentPath) {
          localStorage.setItem(INVITE_CONTENT_PATH_LS_KEY, result.contentPath);
        }
      }

      return result;
    } catch (error) {
      console.error('Failed to claim invite', error);

      return null;
    }
  };

  const completeStoredInvite = async (): Promise<CompleteInviteResponseMutation['completeInviteResponse']> => {
    const inviteId = getStoredInviteId();
    const claimResult = getClaimResult();

    if (!inviteId && claimResult?.status !== InviteResponseStatusEnum.PENDING) {
      return null;
    }

    for (let attempt = 1; attempt <= INVITE_COMPLETE_MAX_RETRIES; attempt += 1) {
      try {
        const { data } = await apolloClient.mutate({
          mutation: CompleteInviteResponseDocument,
        });
        const response = data?.completeInviteResponse ?? null;

        if (response?.status === InviteResponseStatusEnum.SUCCESS_NEW_USER) {
          clearInviteStorage();
        }

        return response;
      } catch (error) {
        console.error(`Failed to complete invite response (attempt ${attempt}/${INVITE_COMPLETE_MAX_RETRIES})`, error);

        if (attempt < INVITE_COMPLETE_MAX_RETRIES) {
          await sleep(INVITE_COMPLETE_RETRY_DELAY_MS);
        }
      }
    }

    return null;
  };

  const syncMyInviteReferrals = async () => {
    try {
      const { data } = await apolloClient.query({
        query: MyInviteReferralsDocument,
        fetchPolicy: 'network-only',
      });
      const referrals = data?.myInviteReferrals ?? [];
      inviteReferrals.value = referrals;
      notifyAboutNewestInviteReferralIfNeeded(referrals);

      return referrals;
    } catch (error) {
      console.error('Failed to sync invite referrals', error);

      return [];
    }
  };

  const resolveInviteAfterAuth = async (
    to: RouteLocationNormalized,
  ): Promise<InviteAuthRedirect | null> => {
    if (getStoredInviteId()) {
      const claimResult = await claimStoredInvite();

      // Onboarding is allowed only after Welcome was seen (inviteWelcomeSeen).
      // Social auth may push to Onboarding first — intercept and show Welcome.
      if (
        claimResult?.status === InviteResponseStatusEnum.PENDING &&
        !isInviteWelcomeSeen() &&
        to.name !== AppScreens.InviteWelcome
      ) {
        return {
          name: AppScreens.InviteWelcome,
        };
      }

      if (claimResult?.status === InviteResponseStatusEnum.IGNORED_EXISTING_USER) {
        const contentPath = claimResult.contentPath || getStoredContentPath();
        if (contentPath && to.name !== AppScreens.InviteWelcome) {
          clearInviteStorage();

          return contentPath.startsWith('/') ? contentPath : `/${contentPath}`;
        }
      }
    }

    if (!referralsSynced) {
      referralsSynced = true;
      await syncMyInviteReferrals();
    }

    return null;
  };

  return {
    lastClaimResult,
    inviteReferrals,
    shouldShowWelcome,
    getStoredInviteId,
    getStoredContentPath,
    isInviteWelcomeSeen,
    markInviteWelcomeSeen,
    getClaimResult,
    persistInviteParams,
    persistInviteFromUrl,
    clearInviteStorage,
    createInvite,
    recordInviteClick,
    claimStoredInvite,
    completeStoredInvite,
    syncMyInviteReferrals,
    resolveInviteAfterAuth,
  };
});
